/**
 * The contents of the learn menu
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingXSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {
    markMinorConversions,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {RegionOverrideResult} from "@khan/data-region-override";
import {doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {maxContainerWidth, mediaQueries} from "@khan/shared-styles";
import {redirect} from "@khan/urls";

import NewBadge from "../new-badge.tsx";
import type {DomainLink, DomainObject} from "../types.ts";

import externalLinkIcon from "@phosphor-icons/core/bold/arrow-square-out-bold.svg";

export type CourseMenuContentProps = {
    domains: Array<DomainObject>;
    // A map from subject slug to subject percentage.
    subjectPercentages?: Record<string, number>;
    // Clicking link should close the learn menu under AppShell.
    onClickLink: () => void;
    // Hitting the escape key should close the learn menu under AppShell
    // and return focus to the dropdown button.
    onEscape: () => unknown;
    // A callback function that allows the LearnMenu to grab a reference to
    // the first navigable child. This is used when a user first navigates
    // down into the menu.
    captureFirstNavigableChildRef?: (node: HTMLLIElement) => unknown;
    // The user's current region, if available.
    region: RegionOverrideResult;
    // True if the user is a teacher or logged-out user. Used to determine if
    // the browse by standards link should be rendered.
    shouldRenderStandardsGuideLink?: boolean;
} & WithMarkConversionProps;

const STANDARDS_URL = "/regions/united-states";

class CoursesMenuContent extends React.Component<CourseMenuContentProps> {
    // The list of links in this dropdown that the user can navigate to using arrow keys.
    navigableChildren: Array<HTMLLIElement | null | undefined> = [];

    setLinkRef: (
        node: HTMLLIElement | null | undefined,
        navigableListIndex: number,
    ) => void = (
        node: HTMLLIElement | null | undefined,
        navigableListIndex: number,
    ) => {
        this.navigableChildren[navigableListIndex] = node;

        if (
            navigableListIndex === 0 &&
            this.props.captureFirstNavigableChildRef &&
            node
        ) {
            this.props.captureFirstNavigableChildRef(node);
        }
    };

    getNavigableIndex: (
        domainIndex: number,
        linkIndex?: number | null | undefined,
        isStandardsGuidesLink?: boolean,
    ) => number = (
        domainIndex: number,
        linkIndex?: number | null,
        isStandardsGuidesLink?: boolean | null | undefined,
    ) => {
        const shouldShowStandardsGuidesLink =
            this.shouldShowStandardsGuidesLink();
        const standardsAlignmentLinkCount = Number(
            shouldShowStandardsGuidesLink,
        );

        // Determine the index for the standards alignment guides link (if rendered)
        if (isStandardsGuidesLink && shouldShowStandardsGuidesLink) {
            return 0;
        }

        // Determine the index for the first domain heading
        if (domainIndex === 0 && linkIndex === null) {
            // If the standards alignment guides link is rendered,
            // the first domain will be second in the sequence (navigable index of 1).
            return standardsAlignmentLinkCount;
        }

        // Calculate the index for items after the first domain heading
        // (count domain headings, their links, and if rendered, the standards alignment guides link)
        // If the linkIndex isn't null, that means we're on a link, which means we're
        // past the heading for this domain.
        const headingsCount =
            linkIndex !== null ? domainIndex + 1 : domainIndex;

        const previousDomains = this.props.domains.slice(0, domainIndex);
        const previousDomainsLinksCount = previousDomains.reduce(
            (total, nextDomain) => {
                return (total += nextDomain.children.length);
            },
            0,
        );
        const currentDomainLinksCount = linkIndex || 0;

        return (
            standardsAlignmentLinkCount +
            headingsCount +
            previousDomainsLinksCount +
            currentDomainLinksCount
        );
    };

    getClickedNavigableIndex: (
        event: React.KeyboardEvent<HTMLAnchorElement>,
    ) => number = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (event.currentTarget.parentNode instanceof HTMLElement) {
            return parseInt(
                // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
                event.currentTarget.parentNode.getAttribute(
                    "data-navigable-index",
                ),
            );
        }

        return -1;
    };

    // TODO(diedra): Consider writing a shared utility that can handle this up-down
    // navigation as this is used in every dropdown menu.
    handleKeyDownLink: (
        sourceEvent: React.KeyboardEvent,
        href: string,
        onFollowLink?: () => Promise<void>,
    ) => void = async (
        sourceEvent: React.KeyboardEvent,
        href: string,
        // Callback for when the the link is followed (enter or space)
        onFollowLink?: () => Promise<void>,
    ) => {
        // We know we're using an anchor element, so let's cast accordingly.
        const event: React.KeyboardEvent<HTMLAnchorElement> =
            sourceEvent as any;
        if (event.key === "Enter" || event.key === " ") {
            await onFollowLink?.();
            // If the clicked link is specifically the standards url, we don't
            // want to prevent default and just let the browser handle it
            if (href === STANDARDS_URL) {
                return;
            }
            // We preventDefault and manually navigate to the link's destination in
            // because the normal link functionality interferes with us closing
            // the learn menu.
            event.preventDefault();
            redirect(href);
            this.props.onClickLink();
        } else if (event.key === "Escape") {
            event.preventDefault();
            this.props.onEscape();
        } else if (event.key === "Tab") {
            // We explicitly don't call event.preventDefault on tabbing so the user can
            // tab to the next node in the DOM tabbing order. We just close the menu
            // since they're leaving
            this.props.onEscape();
        } else if (event.key === "ArrowDown") {
            event.preventDefault();

            const currentIndex = this.getClickedNavigableIndex(event);

            // If we're at the end of the list of links, we do nothing because there's
            // nowhere left to go.
            if (currentIndex === this.navigableChildren.length - 1) {
                return;
            }

            const nextElement = this.navigableChildren[currentIndex + 1];
            if (nextElement && nextElement.firstChild instanceof HTMLElement) {
                nextElement.firstChild.focus();
            }
        } else if (event.key === "ArrowUp") {
            event.preventDefault();

            const currentIndex = this.getClickedNavigableIndex(event);

            // We're at the first header of the first domain going back up
            // to the dropdown button and closing the menu.
            if (currentIndex === 0) {
                this.props.onEscape();
            } else {
                const nextElement = this.navigableChildren[currentIndex - 1];
                if (
                    nextElement &&
                    nextElement.firstChild instanceof HTMLElement
                ) {
                    nextElement.firstChild.focus();
                }
            }
        }
    };

    renderLink: (
        link: DomainLink,
        linkIndex: number,
        domain: DomainObject,
        domainIndex: number,
    ) => React.ReactNode = (
        link: DomainLink,
        linkIndex: number,
        domain: DomainObject,
        domainIndex: number,
    ) => {
        const {subjectPercentages = {}, onClickLink} = this.props;
        const subjectPercentage = subjectPercentages[link.slug];
        const hasProgress = !!subjectPercentage;

        const navigableListIndex = this.getNavigableIndex(
            domainIndex,
            linkIndex,
        );

        const ariaLabel = hasProgress
            ? `${link.translatedTitle} | ${t`${subjectPercentage}%`}`
            : link.translatedTitle;

        const publish = async () => {
            await this.props.publishAnalyticsEvent({
                eventType: "NavBarCourseMenuItemClickedTI",
                eventSchemaVersion: 1,
                currentPageUrlPath: location.pathname,
                contentPath: link.href,
                courseId: link.courseId ?? "",
            });

            await markMinorConversions([
                {
                    id: "global_nav_choose_subject",
                    extra: {
                        domain: domain.slug,
                        subject: link.slug,
                        view: "desktop",
                    },
                },
            ]);
        };

        return (
            <li
                key={linkIndex}
                data-navigable-index={navigableListIndex}
                ref={(node) => this.setLinkRef(node, navigableListIndex)}
            >
                <Link
                    href={link.href}
                    // For "non-content" links, we don't expect to be able to
                    // use client-side navigation, so force a full page reload.
                    skipClientNav={link.nonContentLink}
                    // We set tabbing to -1 so users will be sent to the next
                    // element in the tabbing order outside of this menu on tab.
                    tabIndex={-1}
                    style={styles.linkStyle}
                    onClick={onClickLink}
                    safeWithNav={publish}
                    aria-label={ariaLabel}
                    onKeyDown={(event) => {
                        this.handleKeyDownLink(event, link.href, publish);
                    }}
                >
                    <>
                        {/* Must be included in a <span> for hover styles to work */}
                        <LabelMedium style={styles.subjectLink} tag="span">
                            {link.translatedTitle}
                        </LabelMedium>

                        {hasProgress && (
                            <span className={css(styles.subjectPercentage)}>
                                {/* "mission" is already translated so reuse it. */}
                                {/* TODO: change to "subject" and remove this. */}
                                <Trans>{subjectPercentage}%</Trans>
                            </span>
                        )}

                        {/* We don't render the NEW pill if the user has progress
                    recorded in a subject */}
                        {!hasProgress && link.isNew && (
                            <>
                                <Strut size={spacing.xxxSmall_4} />
                                <NewBadge />
                            </>
                        )}
                    </>
                </Link>
            </li>
        );
    };

    renderHeaderLink: (
        domain: DomainObject,
        domainIndex: number,
    ) => React.ReactNode = (domain: DomainObject, domainIndex: number) => {
        if (!domain.href) {
            return (
                <HeadingXSmall style={styles.headerStyle}>
                    {domain.translatedTitle}
                </HeadingXSmall>
            );
        }

        const navigableListIndex = this.getNavigableIndex(domainIndex, null);

        const publish = async () => {
            await this.props.publishAnalyticsEvent({
                eventType: "NavBarCourseMenuHeaderClickedTI",
                eventSchemaVersion: 1,
                currentPageUrlPath: location.pathname,
                contentPath: domain.href,
                headerId: domain.domainId ?? "",
            });

            await markMinorConversions([
                {
                    id: "global_nav_choose_domain",
                    extra: {
                        domain: domain.slug,
                        view: "desktop",
                    },
                },
            ]);
        };

        return (
            <ul>
                <li
                    data-navigable-index={navigableListIndex}
                    ref={(node) => this.setLinkRef(node, navigableListIndex)}
                >
                    <Link
                        safeWithNav={publish}
                        onClick={this.props.onClickLink}
                        onKeyDown={(event) => {
                            this.handleKeyDownLink(event, domain.href, publish);
                        }}
                        // We set tabbing to -1 so users will be sent to the next element
                        // in the tabbing order outside of this menu on tab.
                        tabIndex={-1}
                        href={domain.href}
                        style={styles.headerLinkStyle}
                    >
                        <>
                            {/* Must be included in a <span> for hover styles to work */}
                            <HeadingXSmall
                                style={styles.headerStyle}
                                tag="span"
                            >
                                {domain.translatedTitle}
                            </HeadingXSmall>

                            {domain.isNew && (
                                <>
                                    <Strut size={spacing.xxxSmall_4} />
                                    <NewBadge />
                                </>
                            )}
                        </>
                    </Link>
                </li>
            </ul>
        );
    };

    shouldShowStandardsGuidesLink(): boolean {
        return (
            !!this.props.shouldRenderStandardsGuideLink &&
            this.props.region.status === "success" &&
            this.props.region.regionCode.toUpperCase().startsWith("US") &&
            RequestInfo.kaLocale === "en"
        );
    }

    // If shouldShowStandardsGuidesLink is true, renders a link to the standards alignment guides above the first domain heading.
    // Currently restricted to logged-out users and (logged-in) teachers determined to have a US region on the English KA locale site.
    // (See useRegionOverride for region identification details.)
    renderStandardsGuidesLink: () => React.ReactNode = () => {
        const translatedTitle = doNotTranslateYet`Browse By Standards`;
        const navigableListIndex = this.getNavigableIndex(0, null, true);

        const publish = async () => {
            await this.props.publishAnalyticsEvent({
                eventType: "NavBarCourseMenuStandardsClicked",
                eventSchemaVersion: 1,
                currentPageUrlPath: location.pathname,
            });
        };

        return (
            <li
                key={navigableListIndex}
                className={css(styles.domain)}
                data-navigable-index={navigableListIndex}
                ref={(node) => this.setLinkRef(node, navigableListIndex)}
            >
                <Link
                    safeWithNav={publish}
                    href={STANDARDS_URL}
                    onKeyDown={(event) => {
                        this.handleKeyDownLink(event, STANDARDS_URL);
                    }}
                    style={styles.standardsHeaderLinkStyle}
                    // We set tabbing to -1 so users will be sent to the next
                    // element in the tabbing order outside of this menu on tab.
                    tabIndex={-1}
                    target="_blank"
                    endIcon={
                        <PhosphorIcon
                            icon={externalLinkIcon}
                            size="small"
                            style={styles.browseByStandardsIcon}
                        />
                    }
                >
                    <HeadingXSmall style={styles.headerStyle} tag="span">
                        {translatedTitle}
                    </HeadingXSmall>
                </Link>
            </li>
        );
    };

    renderDomain: (
        domain: DomainObject,
        domainIndex: number,
    ) => React.ReactNode = (domain: DomainObject, domainIndex: number) => {
        return (
            <li key={domainIndex}>
                <div
                    className={css(styles.domain)}
                    data-test-learn-menu-domain={domain.slug}
                >
                    {this.renderHeaderLink(domain, domainIndex)}

                    <ul
                        className={css(
                            domain.children.length && styles.subjectsList,
                        )}
                    >
                        {domain.children.map((link: DomainLink, linkIndex) =>
                            this.renderLink(
                                link,
                                linkIndex,
                                domain,
                                domainIndex,
                            ),
                        )}
                    </ul>
                </div>
            </li>
        );
    };

    render(): React.ReactNode {
        // HACK(alex): Allow only the split curated learn menu to expand up
        // to 5 columns at full desktop width.
        const upToFiveColumns = this.props.domains.length >= 7;
        return (
            <ul
                className={css(
                    styles.domains,
                    upToFiveColumns && styles.domainsFiveColumns,
                )}
                data-testid="learn-menu"
            >
                {this.shouldShowStandardsGuidesLink() &&
                    this.renderStandardsGuidesLink()}
                {this.props.domains.map(this.renderDomain)}
            </ul>
        );
    }
}

const gapBetweenStackedDomains = spacing.xLarge_32;
const spaceBetweenLinks = spacing.medium_16;

const styles = StyleSheet.create({
    domains: {
        boxSizing: "border-box",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: maxContainerWidth,
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 28,
        paddingBottom: 48 - gapBetweenStackedDomains,
        columnCount: 4,
    },

    domainsFiveColumns: {
        [mediaQueries.xl]: {
            columnCount: 5,
        },
    },

    domain: {
        // disallows column breaks inside the element
        display: "inline-block",
        // Subtract the top padding & the bottom padding of links to retain
        // the proper gap between stacked domains
        marginBottom:
            gapBetweenStackedDomains - spacing.xxxSmall_4 - spaceBetweenLinks,
        paddingTop: spacing.xxxSmall_4,
        wordWrap: "break-word",
        width: "95%",
    },

    subjectsList: {
        paddingTop: spacing.medium_16,
    },

    subjectLink: {
        whiteSpace: "normal",

        // We do want the actual text inside the link to be underlined.
        // (As opposed to the percentage and the NEW badge)
        ":hover": {
            textDecoration: "underline",
        },
    },

    subjectPercentage: {
        color: color.offBlack,
        marginLeft: spacing.xSmall_8,
        textDecoration: "none",
    },

    linkStyle: {
        alignItems: "flex-start",
        display: "inline-flex",
        color: color.blue,
        width: "100%",
        marginBottom: spaceBetweenLinks,
        whiteSpace: "normal",
        textDecoration: "none",
        justifyContent: "space-between",
    },

    headerStyle: {
        marginBottom: 0,

        // We do want the actual text inside the link to be underlined.
        // (As opposed to the percentage and the NEW badge)
        ":hover": {
            textDecoration: "underline",
        },
    },

    headerLinkStyle: {
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        color: color.darkBlue,
        height: 20,
        width: "100%",
        paddingBottom: spacing.xSmall_8,
        whiteSpace: "normal",

        ":active": {
            color: color.darkBlue,
        },

        ":hover": {
            color: color.blue,
        },
    },

    standardsHeaderLinkStyle: {
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        height: 20,
        width: "100%",
        paddingBottom: spacing.xSmall_8,
        whiteSpace: "normal",
    },

    browseByStandardsIcon: {
        color: color.blue,
        marginLeft: spacing.xxxSmall_4,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof CoursesMenuContent,
        CourseMenuContentProps
    >
>;

export default compose(withMarkConversion)(
    CoursesMenuContent,
) as React.ComponentType<ExportProps>;
